/**
 * PoLBanner — Always-visible Proof-of-Life status card at the top of the
 * dashboard. The single most important action in KeepSoul: confirm you're
 * alive on schedule so the legacy delivery is NOT triggered.
 *
 * Tiers (computed locally from pol.next_check_at + pol.status):
 *  - "active"  → > 7 days until next check  → golden dot, primary CTA
 *  - "due"     → ≤ 7 days until next check  → amber dot, urgent copy
 *  - "risk"    → overdue (negative days)    → red dot, red border, alarm
 *
 * Props:
 *  - pol: { status, next_check_at, ... } | null
 *  - onCheckIn: async () => void  (parent handles the API call + state)
 *  - busy: boolean                (disable CTA while saving)
 *  - configHref: string           (where to go for full settings, default /proof-of-life)
 */
import { Link } from "react-router-dom";
import { HeartPulse, AlertTriangle, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";

function classifyPoL(pol) {
  if (!pol) return { tier: "active", days: null };
  // Prefer the canonical state computed by the backend so all dashboard
  // surfaces agree. Fall back to client-side date math only if absent.
  if (pol.state) {
    const map = { active: "active", pending: "due", grace: "risk", overdue: "risk" };
    return { tier: map[pol.state] || "active", days: null };
  }
  if (!pol.next_check_at) return { tier: "active", days: null };
  const next = new Date(pol.next_check_at).getTime();
  const now = Date.now();
  const days = Math.round((next - now) / 86400000);
  if (days < 0) return { tier: "risk", days };
  if (days <= 7) return { tier: "due", days };
  return { tier: "active", days };
}

const TIER_STYLE = {
  active: {
    card: "bg-[rgba(201,163,106,0.06)] border-[rgba(201,163,106,0.22)]",
    dot: "bg-gold",
    text: "text-gold",
  },
  due: {
    card: "bg-[rgba(245,158,11,0.08)] border-[rgba(245,158,11,0.35)]",
    dot: "bg-amber-400",
    text: "text-amber-400",
  },
  risk: {
    card: "bg-[rgba(239,68,68,0.08)] border-[rgba(239,68,68,0.35)]",
    dot: "bg-red-500 animate-pulse",
    text: "text-red-400",
  },
};

export default function PoLBanner({ pol, onCheckIn, busy = false, configHref = "/proof-of-life" }) {
  const { t } = useI18n();
  const { tier } = classifyPoL(pol);
  const style = TIER_STYLE[tier];
  const dateStr = pol?.next_check_at
    ? new Date(pol.next_check_at).toLocaleDateString(undefined, {
        day: "numeric", month: "short", year: "numeric",
      })
    : "";

  const headline = {
    active: t("pol_banner_active"),
    due: t("pol_banner_due"),
    risk: t("pol_banner_risk"),
  }[tier];
  const subline = {
    active: t("pol_banner_active_sub").replace("{DATE}", dateStr),
    due: t("pol_banner_due_sub").replace("{DATE}", dateStr),
    risk: t("pol_banner_risk_sub"),
  }[tier];

  return (
    <div
      className={`glass-card rounded-md border ${style.card} p-5 sm:p-6 mb-6`}
      data-testid="pol-banner"
      data-tier={tier}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="shrink-0 mt-0.5">
            {tier === "risk" ? (
              <AlertTriangle className="w-6 h-6 text-red-400" />
            ) : (
              <HeartPulse className={`w-6 h-6 ${style.text}`} strokeWidth={1.6} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-label text-faded mb-1.5">{t("nav_pol")}</div>
            <div className={`font-display text-xl sm:text-2xl ${style.text} flex items-center gap-2 leading-tight`}>
              <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`} aria-hidden="true" />
              {headline}
            </div>
            <p className="text-sm text-muted mt-1.5">{subline}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0 w-full sm:w-auto">
          <button
            onClick={onCheckIn}
            disabled={busy}
            className="btn-gold w-full sm:w-auto justify-center"
            data-testid="pol-banner-checkin"
          >
            <HeartPulse className="w-4 h-4" /> {t("pol_banner_cta")}
          </button>
          <Link
            to={configHref}
            className="text-sm text-gold-light/80 hover:text-gold-light inline-flex items-center justify-center gap-1.5 px-3 py-2 transition"
            data-testid="pol-banner-config"
          >
            {t("pol_banner_config")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
